import React, { useState, useRef, useEffect } from 'react';
import AnimatedSessionTitle from './AnimatedSessionTitle';
import PropTypes from 'prop-types';
import { Download, Link2 } from 'lucide-react';
import { sessionTimeLabel } from '../utils/sessionTime';

const StreamingSpinner = () => (
  <div className="flex-shrink-0 w-3 h-3 rounded-full border-2 border-t-transparent animate-spin border-brand" />
);

const PinIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16 12V4h1V2H7v2h1v8l-2 2v2h5.2v6h1.6v-6H18v-2l-2-2z" />
  </svg>
);

const ChatSessionItem = ({
  id,
  summary,
  isActive,
  onClick,
  onDelete,
  onPin,
  onRename,
  onShare,
  onDownloadSession,
  isPinned,
  isStreaming,
  isUnread,
  isShared,
  updatedAt,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const handleDelete = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onDelete(id, e);
  };

  const handlePin = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onPin(id);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    onShare(id);
  };

  const handleDownloadSession = (e, format) => {
    e.stopPropagation();
    setMenuOpen(false);
    onDownloadSession(id, format);
  };

  const handleRenameClick = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setRenameValue(summary);
    setIsRenaming(true);
  };

  const commitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== summary) {
      onRename(id, trimmed);
    }
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    else if (e.key === 'Escape') { setIsRenaming(false); }
  };

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleMenuToggle = (e) => {
    e.stopPropagation();
    setMenuOpen((prev) => !prev);
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutsideClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [menuOpen]);

  const getContainerClasses = () => {
    // pl-3 on every row so the active rail lands in reserved space and nothing
    // shifts sideways as selection moves.
    const base = 'group relative flex items-center justify-between gap-2 pl-3 pr-2 py-2 rounded-md transition-colors';
    const cursor = isRenaming ? 'cursor-default' : 'cursor-pointer';
    if (isActive) {
      // A rail and a tint rather than a filled block: with a dozen rows on
      // screen the filled version read as the loudest thing in the sidebar,
      // which is backwards — the list is for scanning, not for admiring the
      // selection.
      return `${base} ${cursor} bg-surface-raised text-fg before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[2px] before:rounded-full before:bg-brand`;
    }
    return `${base} ${cursor} text-fg-secondary hover:bg-surface-raised hover:text-fg`;
  };

  const showMenuButton = (isHovered || menuOpen) && !isRenaming;
  const timeLabel = isRenaming ? '' : sessionTimeLabel(updatedAt);

  const pinIconClass = isPinned && !isHovered && !menuOpen
    ? 'text-fg-muted'
    : 'text-fg-muted hover:text-fg';

  return (
    <div
      className={getContainerClasses()}
      onClick={isRenaming ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      title={isRenaming ? undefined : summary}
    >
      {/* Unread indicator */}
      {isUnread && !isActive && !isRenaming && (
        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mr-1.5 bg-brand" />
      )}

      {/* Title or inline rename input */}
      {isRenaming ? (
        <input
          ref={inputRef}
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          onKeyDown={handleRenameKeyDown}
          onBlur={commitRename}
          onClick={(e) => e.stopPropagation()}
          className="text-sm flex-1 min-w-0 bg-transparent border-b border-line outline-none text-fg placeholder-fg-muted"
        />
      ) : (
        <span className="text-sm truncate flex-1 min-w-0 pr-1">
          <AnimatedSessionTitle title={summary} isActive={isActive} />
        </span>
      )}

      {/* Right-side slot: link badge · spinner · pin icon · three-dots */}
      {!isRenaming && (
        <div className="flex items-center flex-shrink-0" ref={menuRef}>
          {/* A live share link exists for this chat. Passive on purpose —
              managing it lives in "Shared links" under the avatar menu; the
              badge's whole job is that a forgotten link stays visible. */}
          {isShared && (
            <span
              title="This chat has a live share link"
              role="img"
              aria-label="This chat has a live share link"
              className="p-1 text-brand"
            >
              <Link2 aria-hidden="true" className="w-3.5 h-3.5" />
            </span>
          )}

          {isStreaming && !isActive && !isHovered && !menuOpen && (
            <StreamingSpinner />
          )}

          {/* When the titles repeat — and generated titles do, a user asking
              similar questions ends up with a column of the same sentence — the
              time is the only thing telling two rows apart. It stands down for
              the actions on hover so the row never holds both. */}
          {timeLabel && !isStreaming && !showMenuButton && (
            <span className="text-[11px] tabular-nums text-fg-muted">{timeLabel}</span>
          )}

          {/* Pin icon — only rendered when pinned, keeps it out of tab order when hidden */}
          {isPinned && (
            <button
              onClick={handlePin}
              className={`p-1 rounded transition-colors hover:bg-surface-raised ${pinIconClass}`}
              title="Unpin chat"
            >
              <PinIcon className="w-3.5 h-3.5" />
            </button>
          )}

          {/* Three-dots button */}
          <div className="relative flex-shrink-0">
            <div className={`overflow-hidden transition-all duration-150 ${showMenuButton ? 'w-6' : 'w-0'}`}>
              <button
                onClick={handleMenuToggle}
                className="p-1 rounded transition-colors hover:bg-surface-raised text-fg-muted hover:text-fg"
                title="More options"
              >
                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                  <circle cx="5" cy="12" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="19" cy="12" r="1.5" />
                </svg>
              </button>
            </div>

            {menuOpen && (
              <div className="absolute right-0 top-full mt-1 w-36 rounded-md shadow-lg z-50 py-1 bg-surface border border-line">
                {/* Rename */}
                <button
                  onClick={handleRenameClick}
                  className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors text-fg-muted hover:bg-surface-raised hover:text-fg"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Rename
                </button>

                {/* Share */}
                {onShare && (
                  <button
                    onClick={handleShare}
                    className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors text-fg-muted hover:bg-surface-raised hover:text-fg"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342a3 3 0 100-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684zm0-9.316a3 3 0 105.368-2.684 3 3 0 00-5.368 2.684z" />
                    </svg>
                    Share
                  </button>
                )}

                {/* Download the whole chat — only offered on the OPEN
                    session: the transcript is composed from the messages
                    already on screen, so opening it first is the deal. */}
                {onDownloadSession && isActive && (
                  <>
                    <button
                      onClick={(e) => handleDownloadSession(e, 'md')}
                      className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors text-fg-muted hover:bg-surface-raised hover:text-fg"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Chat as Markdown
                    </button>
                    <button
                      onClick={(e) => handleDownloadSession(e, 'pdf')}
                      className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors text-fg-muted hover:bg-surface-raised hover:text-fg"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Chat as PDF
                    </button>
                    {/* The conversation as it looked: bubbles, theme, rows.
                        Markdown and PDF are the answer as a document; this is
                        the chat as a page. */}
                    <button
                      onClick={(e) => handleDownloadSession(e, 'html')}
                      className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors text-fg-muted hover:bg-surface-raised hover:text-fg"
                    >
                      <Download className="w-3.5 h-3.5" />
                      Chat as HTML
                    </button>
                  </>
                )}

                {/* Pin / Unpin */}
                <button
                  onClick={handlePin}
                  className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors text-fg-muted hover:bg-surface-raised hover:text-fg"
                >
                  <PinIcon className="w-3.5 h-3.5" />
                  {isPinned ? 'Unpin' : 'Pin'}
                </button>

                {/* Delete */}
                <button
                  onClick={handleDelete}
                  className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors text-danger hover:bg-surface-raised"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Delete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ChatSessionItem.propTypes = {
  id: PropTypes.string.isRequired,
  summary: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onPin: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onShare: PropTypes.func,
  onDownloadSession: PropTypes.func,
  isPinned: PropTypes.bool,
  isStreaming: PropTypes.bool,
  isUnread: PropTypes.bool,
  isShared: PropTypes.bool,
  updatedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.instanceOf(Date)]),
};

ChatSessionItem.defaultProps = {
  isPinned: false,
  isStreaming: false,
  isUnread: false,
  isShared: false,
};

export default ChatSessionItem;
